/** Spark line chart for compact time-series. Uses {@link getChartColorForSvg} for SVG stroke. */

import { forwardRef } from "react";
import {
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import type { AxisDomain } from "recharts/types/util/types";
import { cn } from "@/lib/utils";
import {
  AvailableChartColors,
  type AvailableChartColorsKeys,
  constructCategoryColors,
  getChartColorForSvg,
  getYAxisDomain,
  getYAxisDomainForSeriesData,
} from "@/lib/utils/chartUtils";
import type { SparkLineChartProps } from "./SparkChart.types";

export const SparkLineChart = forwardRef<HTMLDivElement, SparkLineChartProps>(
  (props, forwardedRef) => {
    const {
      data = [],
      categories = [],
      index,
      colors = AvailableChartColors,
      autoMinValue = false,
      minValue,
      maxValue,
      connectNulls = false,
      className,
      ...other
    } = props;

    const categoryColors = constructCategoryColors(categories, colors);
    const yAxisDomain =
      minValue != null || maxValue != null
        ? getYAxisDomain(autoMinValue, minValue, maxValue)
        : getYAxisDomainForSeriesData(data, categories, { autoMinValue });

    return (
      <div
        ref={forwardedRef}
        aria-hidden
        className={cn("h-12 w-28", className)}
        {...other}
      >
        <ResponsiveContainer>
          <RechartsLineChart
            data={data}
            margin={{
              bottom: 1,
              left: 1,
              right: 1,
              top: 1,
            }}
          >
            <XAxis hide dataKey={index} />
            <YAxis hide domain={yAxisDomain as AxisDomain} />
            {categories.map((category) => {
              const colorKey = categoryColors.get(category) as
                | AvailableChartColorsKeys
                | undefined;
              const color = getChartColorForSvg(colorKey ?? "gray");
              return (
                <Line
                  key={category}
                  dot={false}
                  strokeOpacity={1}
                  name={category}
                  type="linear"
                  dataKey={category}
                  stroke={color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  isAnimationActive={false}
                  connectNulls={connectNulls}
                />
              );
            })}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    );
  },
);

SparkLineChart.displayName = "SparkLineChart";
